/**
 * Drawer "Meu perfil" — auto-serviço do próprio usuário logado.
 * Segue o padrão dos drawers do sistema: header com a cor de destaque,
 * VerticalTabLayout com as abas e footer com as ações de cada aba.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Building2,
  Eye,
  EyeOff,
  Gauge,
  HelpCircle,
  History,
  SlidersHorizontal,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  Monitor,
  Plus,
  Save,
  ShieldCheck,
  User as UserIcon,
  UserCog,
  X,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "../ui/sonner";
import VerticalTabLayout from "../VerticalTabLayout";
import AiUsageTab from "./AiUsageTab";
import { Switch } from "../ui/switch";
import { POSICOES, useQuickPanel } from "../quickpanel/QuickPanelProvider";
import api, { formatApiError } from "../../utils/api";
import { accentBg } from "../../utils/accent";
import { DENSITIES, getDensity, setDensity } from "../../utils/uiDensity";
import { useAuth } from "../../contexts/AuthContext";

const roleLabels = {
  super_admin: "Super Admin",
  admin: "Administrador",
  user: "Operador",
};

const deviceLabel = (ua) => {
  if (!ua) return "Dispositivo desconhecido";
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iOS/i.test(ua)) return "iOS";
  if (/Edg/i.test(ua)) return "Edge · Desktop";
  if (/Chrome/i.test(ua)) return "Chrome · Desktop";
  if (/Firefox/i.test(ua)) return "Firefox · Desktop";
  if (/Safari/i.test(ua)) return "Safari · Desktop";
  return "Navegador";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const EVENT_LABEL = {
  "auth.login": "Acesso",
  "auth.password_changed": "Senha alterada",
  "auth.logout_all": "Sessões encerradas",
};

// Mesmos critérios de Configurações → Usuários → Novo usuário.
const pwdChecks = (pw) => ({
  length: pw.length >= 8,
  upper: /[A-Z]/.test(pw),
  digit: /[0-9]/.test(pw),
  special: /[^A-Za-z0-9]/.test(pw),
});

const STRENGTH_LEVELS = [
  { label: "Muito fraca", color: "#ef4444", width: "20%" },
  { label: "Fraca", color: "#f97316", width: "40%" },
  { label: "Razoável", color: "#f59e0b", width: "60%" },
  { label: "Forte", color: "#22c55e", width: "80%" },
  { label: "Muito forte", color: "#10b981", width: "100%" },
];

export default function ProfileDrawer({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("dados");
  const [density, setDensityState] = useState(getDensity);
  const quickPanel = useQuickPanel();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [changing, setChanging] = useState(false);

  const [history, setHistory] = useState([]);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("dados");
    setName(user?.name || "");
    setPhone(user?.phone || "");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }, [open, user]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/login-history");
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    if (open && tab === "acessos") loadHistory();
  }, [open, tab, loadHistory]);

  const saveData = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, phone });
      await refreshUser();
      toast.success("Dados atualizados");
      onClose();
    } catch (err) {
      toast.error("Erro ao salvar", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e?.preventDefault?.();
    if (newPassword !== confirmPassword) {
      toast.error("A confirmação não confere com a nova senha");
      return;
    }
    setChanging(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Senha alterada", {
        description: "As sessões em outros dispositivos foram encerradas.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error("Erro ao alterar senha", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setChanging(false);
    }
  };

  const logoutAll = async () => {
    setLoggingOutAll(true);
    try {
      await api.post("/auth/logout-all");
      toast.success("Sessões encerradas", { description: "Este dispositivo continua conectado." });
      setConfirmLogoutAll(false);
      loadHistory();
    } catch (err) {
      toast.error("Erro ao encerrar sessões", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoggingOutAll(false);
    }
  };

  const checks = pwdChecks(newPassword);
  const pwdStrong = checks.length && checks.upper && checks.digit && checks.special;
  const pwdValid = currentPassword.length > 0 && pwdStrong && newPassword === confirmPassword;

  const strength = (() => {
    if (!newPassword) return null;
    let score = 0;
    if (newPassword.length >= 8) score++;
    if (newPassword.length >= 12) score++;
    if (checks.upper) score++;
    if (checks.digit) score++;
    if (checks.special) score++;
    return STRENGTH_LEVELS[Math.min(score, 4)];
  })();

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent
          side="right"
          className="w-full sm:w-[45rem] sm:max-w-none flex flex-col gap-0 p-0 [&>button:first-child]:hidden"
          data-testid="profile-drawer"
        >
          {/* Header */}
          <div className="px-6 py-4 text-white flex items-center justify-between shrink-0" style={accentBg()}>
            <SheetTitle data-testid="sheet-title" className="flex items-center gap-2.5 text-white">
              <UserCog className="h-5 w-5" />
              Meu perfil
            </SheetTitle>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors cursor-pointer"
              data-testid="sheet-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Resumo do usuário */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 shrink-0">
            <div className="flex items-center gap-4">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center text-white text-lg font-semibold shrink-0"
                style={accentBg()}
              >
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {[roleLabels[user?.role] || user?.role, user?.email, user?.tenant_name]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </div>
          </div>

          <VerticalTabLayout
            tabs={[
              {
                id: "dados",
                icon: UserIcon,
                label: "Meus dados",
                subtitle: "Seu nome e telefone de contato dentro do sistema.",
              },
              {
                id: "seguranca",
                icon: ShieldCheck,
                label: "Segurança",
                subtitle: "Troque a sua senha de acesso ao painel.",
              },
              {
                id: "preferencias",
                icon: SlidersHorizontal,
                label: "Preferências",
                subtitle: "Ajuste o botão de apoio rápido que fica flutuando na tela.",
              },
              {
                id: "aparencia",
                icon: Monitor,
                label: "Aparência",
                subtitle: "Defina o tamanho dos elementos na tela deste dispositivo.",
              },
              {
                id: "utilizacoes",
                icon: Gauge,
                label: "Utilizações",
                subtitle: "Quanto a sua empresa consumiu de inteligência artificial neste mês.",
              },
              {
                id: "acessos",
                icon: History,
                label: "Acessos",
                subtitle: "Seus últimos acessos e as sessões abertas em outros dispositivos.",
              },
            ]}
            activeTab={tab}
            onChange={setTab}
            sidebarTitle="Perfil"
            testIdPrefix="profile-tab"
          >
            {tab === "dados" && (
              <form onSubmit={saveData} noValidate className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Nome *</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="rounded-lg"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Telefone / WhatsApp</Label>
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(11) 99999-9999"
                      className="rounded-lg"
                      data-testid="profile-phone-input"
                    />
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4 space-y-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                      Não editável por aqui
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail size={14} className="text-gray-400" /> {user?.email}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ShieldCheck size={14} className="text-gray-400" />
                      {roleLabels[user?.role] || user?.role}
                    </div>
                    {user?.tenant_name ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building2 size={14} className="text-gray-400" /> {user.tenant_name}
                      </div>
                    ) : null}
                    <p className="text-xs text-gray-400 pt-1">
                      E-mail e perfil de acesso são alterados por um administrador, em
                      Configurações → Usuários.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
                  <div className="flex-1" />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg gap-1.5"
                    onClick={onClose}
                    data-testid="profile-form-cancel"
                  >
                    <X size={15} />
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving || name.trim().length < 2}
                    className="rounded-lg text-white gap-1.5"
                    style={accentBg()}
                    data-testid="profile-form-save"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save size={15} />
                        Salvar
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {tab === "seguranca" && (
              <form onSubmit={savePassword} noValidate className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Senha atual *</Label>
                    <div className="relative">
                      <Input
                        type={showPwd ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoComplete="current-password"
                        className="rounded-lg pr-10"
                        data-testid="profile-current-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                        onClick={() => setShowPwd(!showPwd)}
                        tabIndex={-1}
                        data-testid="profile-toggle-pwd-btn"
                      >
                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                      Nova senha *
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle size={14} className="text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                            <p className="font-semibold mb-1">Requisitos da senha:</p>
                            <ul className="list-disc pl-3 space-y-0.5">
                              <li>Mínimo 8 caracteres</li>
                              <li>Pelo menos 1 letra maiúscula</li>
                              <li>Pelo menos 1 número</li>
                              <li>Pelo menos 1 caractere especial (!@#$...)</li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      type={showPwd ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                      className="rounded-lg"
                      data-testid="profile-new-password-input"
                    />
                    {strength && (
                      <div className="mt-2" data-testid="profile-password-strength">
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ width: strength.width, backgroundColor: strength.color }}
                          />
                        </div>
                        <p className="text-xs mt-1" style={{ color: strength.color }}>
                          {strength.label}
                        </p>
                      </div>
                    )}
                    <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {[
                        ["length", "Mínimo 8 caracteres"],
                        ["upper", "1 letra maiúscula"],
                        ["digit", "1 número"],
                        ["special", "1 caractere especial"],
                      ].map(([key, label]) => (
                        <li
                          key={key}
                          className={checks[key] ? "text-emerald-600" : "text-gray-400"}
                        >
                          {checks[key] ? "✓" : "•"} {label}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Confirmar nova senha *</Label>
                    <Input
                      type={showPwd ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className="rounded-lg"
                      data-testid="profile-confirm-password-input"
                    />
                    {confirmPassword && confirmPassword !== newPassword ? (
                      <p className="text-xs mt-1 text-red-500">A confirmação não confere.</p>
                    ) : null}
                  </div>

                  <p className="text-xs text-gray-400">
                    Ao trocar a senha, as sessões abertas em outros dispositivos são encerradas.
                    Este dispositivo continua conectado.
                  </p>
                </div>

                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
                  <div className="flex-1" />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg gap-1.5"
                    onClick={onClose}
                    data-testid="profile-security-cancel"
                  >
                    <X size={15} />
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={!pwdValid || changing}
                    className="rounded-lg text-white gap-1.5"
                    style={accentBg()}
                    data-testid="profile-change-password-btn"
                  >
                    {changing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <KeyRound size={15} />
                        Alterar senha
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {tab === "preferencias" && (
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-testid="profile-preferences">
                <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Botão de apoio rápido</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Atalho flutuante para criar tarefas e notas em qualquer tela.
                    </p>
                  </div>
                  <Switch
                    checked={quickPanel.prefs?.enabled !== false}
                    onCheckedChange={(v) =>
                      quickPanel
                        .salvarPrefs({ ...quickPanel.prefs, enabled: !!v })
                        .then(() => toast.success(v ? "Apoio rápido ativado." : "Apoio rápido desativado."))
                        .catch(() => toast.error("Não foi possível salvar a preferência."))
                    }
                    data-testid="profile-quick-panel-enabled"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Posição na tela</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Escolha o canto onde o botão + vai ficar. Vale para todas as telas.
                  </p>
                  <div className="grid grid-cols-3 gap-2.5">
                    {POSICOES.map(([id, rotulo]) => {
                      const ativo = (quickPanel.prefs?.position || "bottom-center") === id;
                      const [v, h] = id.split("-");
                      return (
                        <button
                          key={id}
                          type="button"
                          disabled={quickPanel.prefs?.enabled === false}
                          onClick={() =>
                            quickPanel
                              .salvarPrefs({ ...quickPanel.prefs, position: id })
                              .then(() => toast.success("Posição atualizada."))
                              .catch(() => toast.error("Não foi possível salvar a preferência."))
                          }
                          className={`rounded-lg border p-2 text-left transition-all disabled:opacity-50 ${
                            ativo
                              ? "border-transparent text-white shadow-sm"
                              : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                          style={ativo ? { backgroundColor: "#111827" } : undefined}
                          data-testid={`profile-quick-panel-position-${id}`}
                        >
                          <span
                            className={`block h-9 rounded relative overflow-hidden ${
                              ativo ? "bg-white/25" : "bg-gray-100"
                            }`}
                          >
                            <span
                              className={`absolute h-3 w-3 rounded-full flex items-center justify-center ${
                                ativo ? "bg-white" : "bg-gray-400"
                              } ${v === "top" ? "top-1.5" : "bottom-1.5"} ${
                                h === "left"
                                  ? "left-1.5"
                                  : h === "right"
                                    ? "right-1.5"
                                    : "left-1/2 -translate-x-1/2"
                              }`}
                            >
                              <Plus
                                size={8}
                                strokeWidth={3}
                                className={ativo ? "text-gray-900" : "text-white"}
                              />
                            </span>
                          </span>
                          <span
                            className={`block text-[11px] mt-1.5 leading-tight ${
                              ativo ? "font-semibold text-white" : "text-gray-500"
                            }`}
                          >
                            {rotulo}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {tab === "aparencia" && (
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-testid="profile-appearance">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Tamanho da interface</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    O modo compacto reduz textos, espaçamentos e ícones para caber mais informação
                    na tela. A escolha vale só para este navegador.
                  </p>
                  <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1 w-fit">
                    {DENSITIES.map((d) => {
                      const ativo = density === d.value;
                      return (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => {
                            setDensity(d.value);
                            setDensityState(d.value);
                            toast.success(
                              d.value === "compact"
                                ? "Interface no modo compacto."
                                : "Interface no tamanho normal.",
                            );
                          }}
                          aria-pressed={ativo}
                          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors ${
                            ativo ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"
                          }`}
                          data-testid={`profile-density-${d.value}`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {tab === "utilizacoes" && <AiUsageTab />}

            {tab === "acessos" && (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-2">
                      Últimos acessos
                    </p>
                    {history.length === 0 ? (
                      <p className="text-sm text-gray-400">Nenhum acesso registrado ainda.</p>
                    ) : (
                      <ul
                        className="divide-y divide-gray-100 rounded-lg border border-gray-200"
                        data-testid="profile-login-history"
                      >
                        {history.map((h, i) => (
                          <li key={`${h.created_at}-${i}`} className="flex items-start gap-2.5 px-3 py-2">
                            <span className="mt-0.5 text-gray-400 shrink-0">
                              {h.event_type === "auth.login" ? (
                                <Monitor size={14} />
                              ) : h.event_type === "auth.logout_all" ? (
                                <LogOut size={14} />
                              ) : (
                                <KeyRound size={14} />
                              )}
                            </span>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-700">
                                {EVENT_LABEL[h.event_type] || "Evento"} · {fmtDate(h.created_at)}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {deviceLabel(h.user_agent)} · IP {h.ip || "—"}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
                    <p className="text-sm font-medium text-gray-800">Sair de todos os dispositivos</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Encerra as sessões abertas em outros navegadores e computadores. Este
                      dispositivo continua conectado.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
                  <div className="flex-1" />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg gap-1.5"
                    onClick={onClose}
                    data-testid="profile-access-close"
                  >
                    <X size={15} />
                    Fechar
                  </Button>
                  <Button
                    type="button"
                    className="rounded-lg gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => setConfirmLogoutAll(true)}
                    data-testid="profile-logout-all-btn"
                  >
                    <LogOut size={15} />
                    Sair de todos os dispositivos
                  </Button>
                </div>
              </div>
            )}
          </VerticalTabLayout>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmLogoutAll} onOpenChange={setConfirmLogoutAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5 text-red-500" />
              Encerrar sessões
            </AlertDialogTitle>
            <AlertDialogDescription>
              Todas as sessões abertas em <strong>outros dispositivos</strong> serão encerradas e
              será necessário entrar novamente. Este dispositivo continua conectado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-logout-all-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              onClick={(e) => {
                e.preventDefault();
                logoutAll();
              }}
              data-testid="confirm-logout-all-action"
            >
              {loggingOutAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut size={15} />}
              Encerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
